import { useAxios } from '@healthblocks-io/core'
import { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Modal } from '@/@bothrs-ui/web/components/modal/Modal'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'

type FileAnswerProps = {
  title?: string
  description?: string
  files?: {
    url: string
  }[]
}

export const FileAnswer = ({ title, description, files }: FileAnswerProps) => {
  const theme = useTheme()
  const axios = useAxios()
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [blobUrls, setBlobUrls] = useState<Record<string, string>>({})

  const filesWithUrls = files?.filter((file) => file.url) || []

  useEffect(() => {
    const urls = (files?.filter((file) => file.url) || []).map((file) => file.url)

    if (urls.length === 0) {
      setBlobUrls({})
      return
    }

    let isCancelled = false
    const abortController = new AbortController()
    const objectUrls: string[] = []

    void Promise.all(
      urls.map(async (url) => {
        try {
          // SAS URLs are self-authenticated via query params; fetching via axios
          // adds an Authorization header that triggers a CORS preflight Azure
          // Blob Storage does not satisfy. Use the URL directly as the img src.
          if (url.includes('.blob.core.windows.net')) {
            return { url, objectUrl: url }
          }

          const response = await axios.get<Blob>(url, {
            responseType: 'blob',
            signal: abortController.signal,
          })
          const objectUrl = URL.createObjectURL(response.data)

          if (isCancelled) {
            URL.revokeObjectURL(objectUrl)
            return { url, objectUrl: null }
          }

          objectUrls.push(objectUrl)
          return { url, objectUrl }
        } catch {
          return { url, objectUrl: null }
        }
      }),
    ).then((results) => {
      if (!isCancelled) {
        setBlobUrls(
          Object.fromEntries(
            results
              .filter((r): r is { url: string; objectUrl: string } => r.objectUrl !== null)
              .map((r) => [r.url, r.objectUrl]),
          ),
        )
      }
    })

    return () => {
      isCancelled = true
      abortController.abort()
      objectUrls.forEach((u) => URL.revokeObjectURL(u))
    }
  }, [axios, files])

  const handleImageClick = (url: string) => {
    console.log('handleImageClick', url)
    setSelectedImage(url)
    setIsLoading(true)
  }

  const handleCloseModal = () => {
    setSelectedImage(null)
    setIsLoading(false)
  }

  const handleImageLoad = () => {
    setIsLoading(false)
  }

  return (
    <FlexColumn gap={theme.UI.Spacing.Space2}>
      <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darkest}>
        {`${title && description ? `${title}: ` : title || ''}${description}`}
      </Span>
      {filesWithUrls.map((file, index) =>
        blobUrls[file.url] ? (
          <StyledImage
            key={index}
            src={blobUrls[file.url]}
            alt='file'
            onClick={() => handleImageClick(blobUrls[file.url])}
          />
        ) : null,
      )}

      <Modal isVisible={!!selectedImage} onClose={handleCloseModal} fullScreen backdropColor='rgba(0, 0, 0, 0.85)'>
        <StModalContent>
          {isLoading && <StyledLoadingSpinner />}
          <StyledFullScreenImage
            src={selectedImage!}
            alt='Full screen'
            onClick={(event) => event.stopPropagation()}
            onLoad={handleImageLoad}
          />
          <StyledCloseButton onClick={handleCloseModal} />
        </StModalContent>
      </Modal>
    </FlexColumn>
  )
}

const StModalContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledImage = styled.img`
  border-radius: 8px;
  height: 200px;
  object-fit: cover;
  cursor: pointer;
`

const StyledFullScreenImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
`

const StyledCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: white;
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`

const StyledLoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
`
